import React, { useState } from 'react';

// Material UI
import { styled, Box, Button, TextField, Grid } from '@mui/material';
import { Card, CardHeader, CardContent, CardActions } from '@mui/material';
import LaunchIcon from '@mui/icons-material/Launch';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import GetAppIcon from '@mui/icons-material/GetApp';

// Utility
import { useNotification } from 'context/notification';
import * as PromoteAPI from '../PromoteAPI';
import { downloadFile } from 'utility/browser';

export default function HostLink({ hostId }) {
    const [linkCopied, setLinkCopied] = useState(false);

    const [qrDownloading, setQrDownloading] = useState(false);

    const { notify } = useNotification();

    // Generate Storefront Link
    const hostLink = `${process.env.REACT_APP_STORE_URL}/host/${hostId}`;

    const copyHost = async () => {
        setLinkCopied(true);
        try {
            await navigator.clipboard.writeText(hostLink);
        } catch (e) {
            notify.error('Unable to copy to clipboard');
        }
    };

    const downloadHostQR = async () => {
        if (qrDownloading) return;
        setQrDownloading(true);

        PromoteAPI.getHostQRCode(hostId)
            .then((file) => {
                downloadFile(`qr-code-all-events-${hostId}.png`, file);
                setQrDownloading(false);
            })
            .catch((e) => {
                notify.error('Unable to download qr code');
                setQrDownloading(false);
            });
    };

    return (
        <Card>
            <CardHeader
                title="Organizer Page"
                subheader="This link directs the public to your organizer page, which lists all of your upcoming events."
            />
            <CardContent>
                <LinkContainer
                    helperText={linkCopied ? 'Copied to clipboard' : ' '}
                    value={hostLink}
                />
            </CardContent>
            <CardActions>
                <Grid container spacing={1} sx={{ display: { xs: 'flex', sm: 'none' } }}>
                    <Grid xs={6} item>
                        <LinkButton
                            fullWidth
                            startIcon={<LaunchIcon />}
                            href={hostLink}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Visit Link
                        </LinkButton>
                    </Grid>
                    <Grid xs={6} item>
                        <LinkButton fullWidth startIcon={<FileCopyIcon />} onClick={copyHost}>
                            Copy Link
                        </LinkButton>
                    </Grid>
                    <Grid xs={12} item>
                        <LinkButton
                            fullWidth
                            startIcon={<GetAppIcon />}
                            onClick={downloadHostQR}
                            disabled={qrDownloading}
                        >
                            Download QR Code
                        </LinkButton>
                    </Grid>
                </Grid>
                <Box sx={{ display: { xs: 'none', sm: 'flex' } }}>
                    <LinkButton
                        sx={{ ml: 1 }}
                        startIcon={<LaunchIcon />}
                        href={hostLink}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Visit Link
                    </LinkButton>
                    <LinkButton sx={{ ml: 1 }} startIcon={<FileCopyIcon />} onClick={copyHost}>
                        Copy Link
                    </LinkButton>
                    <LinkButton
                        sx={{ ml: 1 }}
                        startIcon={<GetAppIcon />}
                        onClick={downloadHostQR}
                        disabled={qrDownloading}
                    >
                        Download QR Code
                    </LinkButton>
                </Box>
            </CardActions>
        </Card>
    );
}

const LinkContainer = styled(TextField)(({ theme }) => ({
    borderColor: theme.palette.secondary.main,
    marginHorizontal: theme.spacing(2),
}));
LinkContainer.defaultProps = {
    variant: 'outlined',
    fullWidth: true,
    onFocus: (e) => e.target.select(),
};

const LinkButton = styled(Button)(({ theme }) => ({}));
LinkButton.defaultProps = { variant: 'outlined', color: 'secondary' };
